import React, { useState, useEffect, useMemo } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';

import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaEye, FaBox, FaToggleOn, FaToggleOff, FaFileExcel, FaUpload, FaSearch, FaSort, FaSortUp, FaSortDown, FaFilter, FaInfoCircle } from 'react-icons/fa';
import axios from 'axios';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyProducts = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [pageGroup, setPageGroup] = useState(0); // لتتبع مجموعة الصفحات الحالية
    const [excelFile, setExcelFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
const [subCategories, setSubCategories] = useState([]);
const [filters, setFilters] = useState({
    status: '',
    subcategory_id: ''
});
const [selectedProducts, setSelectedProducts] = useState([]);
const [isBulkDeleting, setIsBulkDeleting] = useState(false);
const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
const [isAllSelected, setIsAllSelected] = useState(false);
const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);
    useEffect(() => {

        setTitle('المنتجات');
        fetchSubCategories();
    }, [setTitle]);


    // useEffect واحد فقط للتحميل والبحث
    useEffect(() => {
        const userRole = getUserRole();
        if (userRole === 'pharmacy_branch' || userRole === 'pharmacy') {
            // تأخير البحث قليلاً لتجنب الطلبات الكثيرة أثناء الكتابة
            const timer = setTimeout(() => {
                fetchProducts();
            }, searchTerm ? 500 : 0); // 500ms للبحث، 0ms للتحميل العادي
            
            return () => clearTimeout(timer);
        } else {
            // للـ roles الأخرى، تحميل مباشر
            fetchProducts();
        }
    }, [currentPage, searchTerm, filters]);

    // إعادة الصفحة إلى 1 عند تغيير البحث أو الفلاتر وتفريغ الاختيارات
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
            setPageGroup(0);
        }
        setSelectedProducts([]);
        setIsAllSelected(false);
    }, [searchTerm, filters]);
  const fetchSubCategories = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/pharmacy/sub-categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            // التحقق من وجود البيانات مباشرة
            if (response.data.data && Array.isArray(response.data.data)) {
                setSubCategories(response.data.data);
            } else {
                setSubCategories([]);
            }
        } catch (error) {
            showErrorToast('فشل في جلب الفئات الفرعية');
        }
    };
    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchProducts = async (isSearching = false) => {
        try {
            // إذا كان التحميل الأولي، استخدم loading، وإلا استخدم searching
            if (isInitialLoad) {
                setLoading(true);
            } else {
                setSearching(true);
            }
            
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                throw new Error('لا يوجد token للمصادقة');
            }

            // تحديد endpoint حسب نوع المستخدم
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products`;
            }

            // تجهيز المعاملات
            const params = {
                page: currentPage,
            };

            if (searchTerm.trim()) {
                params.search = searchTerm;
            }

            if (filters.subcategory_id) {
                params.subcategory_id = filters.subcategory_id;
            }

            if (filters.status) {
                params.status = filters.status === 'active' ? 1 : 0;
            }

            const response = await axios.get(endpoint, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 'failed' || response.data.status === 'faild') {
                throw new Error(response.data.message || 'فشل في جلب المنتجات');
            }

            // تحديد هيكل البيانات حسب نوع المستخدم
            if (userRole === 'pharmacy_branch') {
                // لـ pharmacy_branch البيانات تأتي في response.data.data.data
                setProducts(response.data.data.data || []);
                // حفظ معلومات الـ pagination
                const paginationData = response.data.data.pagination || null;
                setPagination(paginationData);
                
                // تحديث pageGroup بناءً على الصفحة الحالية
                if (paginationData) {
                    const totalPages = paginationData.total_pages || paginationData.last_page || 1;
                    const pagesPerGroup = Math.ceil(totalPages / 2);
                    const currentPage = paginationData.current_page || 1;
                    const newPageGroup = Math.floor((currentPage - 1) / pagesPerGroup);
                    setPageGroup(newPageGroup);
                }
            } else {
                // لـ pharmacy البيانات تأتي في response.data.data[0] مع pagination في response.data.data[1]
                const productsData = response.data.data[0] || [];
                const paginationData = response.data.data[1] || null;
                setProducts(productsData);
                setPagination(paginationData);
                
                // تحديث pageGroup بناءً على الصفحة الحالية
                if (paginationData) {
                    const totalPages = paginationData.total_pages || paginationData.last_page || 1;
                    const pagesPerGroup = Math.ceil(totalPages / 2);
                    const currentPage = paginationData.current_page || 1;
                    const newPageGroup = Math.floor((currentPage - 1) / pagesPerGroup);
                    setPageGroup(newPageGroup);
                }
            }
        } catch (error) {
            setError(-error.response?.data?.message || error.message || 'فشل في جلب المنتجات');
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب المنتجات');
        } finally {
            setLoading(false);
            setSearching(false);
            setIsInitialLoad(false);
        }
    };

    const handleDelete = (productId, productName) => {
        setDeleteTarget({ id: productId, name: productName });
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget?.id) return;
        try {
            setDeletingId(deleteTarget.id);
            const token = getAuthToken();
            const userRole = getUserRole();
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products/${deleteTarget.id}`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products/${deleteTarget.id}`;
            }
            await axios.delete(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            showSuccessToast('تم حذف المنتج بنجاح');
            setDeleteModalOpen(false);
            setDeleteTarget(null);
            fetchProducts();
        } catch (error) {
            showErrorToast('فشل في حذف المنتج');
        } finally {
            setDeletingId(null);
        }
    };

    const handleToggleProductStatus = async (productId, currentStatus) => {
        try {
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                showErrorToast('لا يوجد token للمصادقة');
                return;
            }

            const newStatus = currentStatus === 1 ? 0 : 1;
    
            
            // تحديد endpoint حسب نوع المستخدم
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products/${productId}/status?status=${newStatus}`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products/${productId}/toggle-status`;
            }
            
            if (userRole === 'pharmacy_branch') {
                // لـ pharmacy_branch استخدام POST مع query parameters في URL
                await axios.post(endpoint, {}, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                        'Content-Type': 'application/json',
                    },
                });
            } else {
                // لـ pharmacy و store استخدام POST مع body
                await axios.post(endpoint, {
                    enable: newStatus
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                        'Content-Type': 'application/json',
                    },
                });
            }

            showSuccessToast(newStatus === 1 ? 'تم تفعيل المنتج بنجاح' : 'تم إلغاء تفعيل المنتج بنجاح');
            fetchProducts(); // إعادة تحميل قائمة المنتجات
        } catch (error) {
            showErrorToast('فشل في تغيير حالة المنتج');
        }
    };

    const handleToggleStatus = async (productId, currentStatus) => {
        try {
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                showErrorToast('لا يوجد token للمصادقة');
                return;
            }

            const newStatus = currentStatus === 1 ? 0 : 1;
            
            // تحديد endpoint حسب نوع المستخدم
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products/${productId}/toggle-status`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products/${productId}/toggle-status`;
            }
            
            await axios.post(endpoint, {
                enable: newStatus
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'application/json',
                },
            });

            showSuccessToast(newStatus === 1 ? 'تم تفعيل المنتج بنجاح' : 'تم إلغاء تفعيل المنتج بنجاح');
            fetchProducts(); // إعادة تحميل قائمة المنتجات
        } catch (error) {
            showErrorToast('فشل في تغيير حالة المنتج');
        }
    };
    const handleBulkDeleteClick = () => {
        if (selectedProducts.length > 0) {
            setBulkDeleteModalOpen(true);
        }
    };

    const confirmBulkDelete = async () => {
        if (selectedProducts.length === 0) return;

        try {
            setIsBulkDeleting(true);
            const token = getAuthToken();

            const response = await axios.delete(`${BASE_URL}/pharmacy/products/delete/bulk`, {
                params: {
                    product_ids: selectedProducts
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 'success' || response.data.status === 'successful' || response.data.status === 200) {
                showSuccessToast('تم حذف المنتجات بنجاح');
                setSelectedProducts([]);
                fetchProducts();
                setBulkDeleteModalOpen(false);
            } else {
                showErrorToast(response.data.message || 'فشل في حذف المنتجات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في حذف المنتجات');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const handleSelectRow = (productId) => {
        setSelectedProducts(prev => {
            const isSelected = prev.includes(productId);
            const newList = isSelected 
                ? prev.filter(id => id !== productId) 
                : [...prev, productId];
            
            // إذا قمنا بإلغاء تحديد عنصر، فلا يمكن أن يكون "تحديد الكل" نشطاً
            if (isSelected && isAllSelected) {
                setIsAllSelected(false);
            }
            
            return newList;
        });
    };

    const handleSelectAll = async (currentlyAllSelected) => {
        if (currentlyAllSelected) {
            setSelectedProducts([]);
            setIsAllSelected(false);
        } else {
            try {
                setIsFetchingAllIds(true);
                const token = getAuthToken();
                const userRole = getUserRole();
                
                let endpoint;
                if (userRole === 'pharmacy_branch') {
                    endpoint = `${BASE_URL}/pharmacy/branch/products`;
                } else {
                    endpoint = `${BASE_URL}/pharmacy/products`;
                }

                const params = {
                    per_page: 10000, // جلب كل العناصر
                    page: 1
                };

                if (searchTerm.trim()) params.search = searchTerm;
                if (filters.subcategory_id) params.subcategory_id = filters.subcategory_id;
                if (filters.status) params.status = filters.status === 'active' ? 1 : 0;

                const response = await axios.get(endpoint, {
                    params,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                });

                let allIds = [];
                if (userRole === 'pharmacy_branch') {
                    allIds = (response.data.data.data || []).map(p => p.id);
                } else {
                    allIds = (response.data.data[0] || []).map(p => p.id);
                }

                setSelectedProducts(allIds);
                setIsAllSelected(true);
            } catch (error) {
                showErrorToast('فشل في جلب كل معرفات المنتجات');
            } finally {
                setIsFetchingAllIds(false);
            }
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        
        // تحديث pageGroup بناءً على الصفحة المختارة
        const totalPages = pagination?.total_pages || pagination?.last_page || 1;
        const pagesPerGroup = Math.ceil(totalPages / 2);
        const newPageGroup = Math.floor((page - 1) / pagesPerGroup);
        setPageGroup(newPageGroup);
    };

    // Column helper for table configuration
    const columnHelper = createColumnHelper();

    // Table columns configuration
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'select',
                header: ({ table }) => {
                    return (
                        <div className="text-center">
                            {isFetchingAllIds ? (
                                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                            ) : (
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isAllSelected}
                                    onChange={() => handleSelectAll(isAllSelected)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                            )}
                        </div>
                    );
                },
                cell: ({ row }) => {
                    const productId = row.original.id;
                    const isSelected = selectedProducts.includes(productId);
                    return (
                        <div className="text-center">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isSelected}
                                onChange={() => handleSelectRow(productId)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                        </div>
                    );
                },
                size: 50,
            }),
            columnHelper.accessor('code', {
                id: 'code',
                header: 'كود المنتج',
                cell: ({ getValue, row }) => {
                    const code = getValue() ?? row.original.product_code ?? row.original.sku ?? null;
                    return (
                        <span className="fw-semibold">{code || '-'}</span>
                    );
                },
                size: 140,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'اسم المنتج',
                cell: ({ getValue, row }) => {
                    const productName = getValue() || row.original.title || 'منتج بدون اسم';
                    return (
                        <div 
                            className="text-center" 
                            style={{ 
                                maxWidth: '250px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                margin: '0 auto'
                            }}
                            title={productName}
                        >
                            <h6 className="mb-0 fw-semibold" style={{ 
                                fontSize: '14px',
                                textAlign: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {productName}
                            </h6>
                        </div>
                    );
                },
                size: 400,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'الحالة',
                cell: ({ getValue, row }) => {
                    const status = getValue() || row.original.enable;
                    const isActive = status === true || status === 1;
                    return (
                        <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`} style={{ color: 'white' }}>
                            {isActive ? 'مفعل' : 'غير مفعل'}
                        </span>
                    );
                },
                filterFn: (row, id, value) => {
                    const status = row.original.status || row.original.enable;
                    const isActive = status === true || status === 1;
                    return value === 'all' || (value === 'active' && isActive) || (value === 'inactive' && !isActive);
                },
                size: 150,
            }),
            columnHelper.accessor(
                (row) => row.subcategory ?? row.subcategory_id,
                {
                    id: 'subcategory',
                    header: 'الفئة الفرعية',
                    cell: ({ row }) => {
                        const direct = row.original.subcategory;
                        if (direct != null && String(direct).trim() !== '') {
                            return (
                                <span title={String(direct)}>{direct}</span>
                            );
                        }
                        const subCategoryId = row.original.subcategory_id;
                        const subCategory = subCategories.find(
                            (cat) => cat.id === subCategoryId
                        );
                        return subCategory ? subCategory.name : '—';
                    },
                    filterFn: (row, id, value) => {
                        if (value === 'all') return true;
                        const subCategoryId = row.original.subcategory_id;
                        if (subCategoryId != null && subCategoryId !== '') {
                            return String(subCategoryId) === String(value);
                        }
                        const selected = subCategories.find(
                            (cat) => String(cat.id) === String(value)
                        );
                        return Boolean(
                            selected &&
                                row.original.subcategory === selected.name
                        );
                    },
                    size: 200,
                }
            ),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const product = row.original;
                    const userRole = getUserRole();
                    const isActive = product.status === true || product.status === 1 || product.enable === 1;
                    
                    return (
                        <div className="action-buttons d-flex gap-2 flex-wrap justify-content-center">
                            <button
                                className="btn btn-sm btn-action"
                                style={{
                                    backgroundColor: '#17a2b8',
                                    borderColor: '#17a2b8',
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '6px 12px',
                                    borderRadius: '8px'
                                }}
                                title="عرض التفاصيل"
                                onClick={() => navigate(`/pharmacy-products/${product.id}`)}
                            >
                                <FaEye className="mx-1" />
                                عرض
                            </button>
                            
                            {userRole === 'pharmacy_branch' && (
                                <button
                                    className="btn btn-sm btn-toggle"
                                    style={{
                                        backgroundColor: isActive ? '#dc3545' : '#28a745',
                                        borderColor: isActive ? '#dc3545' : '#28a745',
                                        color: 'white',
                                        fontSize: '12px',
                                        padding: '6px 12px',
                                        borderRadius: '8px'
                                    }}
                                    title={isActive ? 'إلغاء التفعيل' : 'تفعيل'}
                                    onClick={() => handleToggleProductStatus(product.id, product.status === true ? 1 : product.status || product.enable || 0)}
                                >
                                    {isActive ? (
                                        <>
                                            <FaToggleOff className="mx-1" />
                                            إلغاء التفعيل
                                        </>
                                    ) : (
                                        <>
                                            <FaToggleOn className="mx-1" />
                                            تفعيل
                                        </>
                                    )}
                                </button>
                            )}
                            
                            {userRole !== 'pharmacy_branch' && (
                                <>
                                    <button
                                        className="btn btn-sm btn-action"
                                        style={{
                                            backgroundColor: '#184194',
                                            borderColor: '#184194',
                                            color: 'white',
                                            fontSize: '12px',
                                            padding: '6px 12px',
                                            borderRadius: '8px'
                                        }}
                                        title="تعديل"
                                        onClick={() => navigate(`/pharmacy-products/edit/${product.id}`)}
                                    >
                                        <FaEdit className="mx-1" />
                                        تعديل
                                    </button>
                                    <button
                                        className="btn btn-sm btn-toggle"
                                        style={{
                                            backgroundColor: isActive ? '#dc3545' : '#28a745',
                                            borderColor: isActive ? '#dc3545' : '#28a745',
                                            color: 'white',
                                            fontSize: '12px',
                                            padding: '6px 12px',
                                            borderRadius: '8px'
                                        }}
                                        title={isActive ? 'إلغاء التفعيل' : 'تفعيل'}
                                        onClick={() => handleToggleStatus(product.id, product.status === true ? 1 : product.status || product.enable || 0)}
                                    >
                                        {isActive ? (
                                            <>
                                                <FaToggleOff className="mx-1" />
                                                إلغاء التفعيل
                                            </>
                                        ) : (
                                            <>
                                                <FaToggleOn className="mx-1" />
                                                تفعيل
                                            </>
                                        )}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-action"
                                        style={{
                                            backgroundColor: '#dc3545',
                                            borderColor: '#dc3545',
                                            color: 'white',
                                            fontSize: '12px',
                                            padding: '6px 12px',
                                            borderRadius: '8px'
                                        }}
                                        title="حذف"
                                        onClick={() => handleDelete(product.id, product.name || product.title || 'منتج بدون اسم')}
                                    >
                                        <FaTrash className="mx-1" />
                                        حذف
                                    </button>
                                </>
                            )}
                        </div>
                    );
                },
                size: 500,
            }),
        ],
        [
            navigate,
            getUserRole,
            handleToggleProductStatus,
            handleToggleStatus,
            handleDelete,
            subCategories,
        ]
    );

    // Table configuration
    const table = useReactTable({
        data: products || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter: searchTerm,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setSearchTerm,
        manualFiltering: true, // تفعيل الفلترة اليدوية (Server-side)
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const handleExcelFileChange = (event) => {
        const file = event.target.files[0];
        setUploadError(null); // مسح الأخطاء السابقة
        if (file) {
            // التحقق من نوع الملف
            const allowedTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
                'application/vnd.ms-excel', // .xls
                'text/csv' // .csv
            ];
            
            if (allowedTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
                setExcelFile(file);
            } else {
                setUploadError('يرجى اختيار ملف Excel صالح (.xlsx, .xls, .csv)');
                event.target.value = ''; // مسح الملف المختار
            }
        }
    };

    const handleExcelUpload = async () => {
        if (!excelFile) {
            setUploadError('يرجى اختيار ملف Excel أولاً');
            return;
        }

        try {
            setIsUploading(true);
            setUploadError(null); // مسح الأخطاء السابقة
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                setUploadError('لا يوجد token للمصادقة');
                return;
            }

            // إنشاء FormData لإرسال الملف
            const formData = new FormData();
            formData.append('file', excelFile);

            // تحديد endpoint حسب نوع المستخدم
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products/import`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products/import`;
            }

            const response = await axios.post(endpoint, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success' || response.data.status === 'successful' || response.data.status === 201) {
                const successMessage = response.data.message || 'تم رفع ملف Excel بنجاح';
                const importedCount = response.data.data?.imported || 0;
                // بعض الـ APIs تُرجع errors ككائن {row: [msgs]}
                const rawErrors = response.data.data?.errors || [];
                const errors =
                    Array.isArray(rawErrors)
                        ? rawErrors
                        : (rawErrors && typeof rawErrors === 'object')
                        ? Object.values(rawErrors).flatMap((list) =>
                              Array.isArray(list) ? list : [String(list)]
                          )
                        : [];
                
                let finalMessage = successMessage;
                if (importedCount > 0) {
                    finalMessage += ` (تم استيراد ${importedCount} منتج)`;
                }
                
                // إذا كان هناك أخطاء في بعض المنتجات
                if (errors.length > 0) {
                    finalMessage += `\n\nتحذير: ${errors.length} منتج لم يتم استيراده بسبب أخطاء`;
                }
                
                showSuccessToast(finalMessage);
                setExcelFile(null);
                setShowUploadModal(false);
                setUploadError(null);
                // إعادة تحميل قائمة المنتجات
                fetchProducts();
            } else {
                // معالجة الأخطاء التفصيلية
                let errorMessage = response.data.message || 'فشل في رفع ملف Excel';
                
                // نمط 422 مع data.errors ككائن صفوف
                if (response.data?.data?.errors && typeof response.data.data.errors === 'object') {
                    const rows = response.data.data.errors;
                    const lines = [];
                    Object.values(rows).forEach((msgs) => {
                        if (Array.isArray(msgs)) {
                            lines.push(...msgs);
                        } else if (msgs) {
                            lines.push(String(msgs));
                        }
                    });
                    if (lines.length > 0) {
                        errorMessage += '\n\nالتفاصيل:\n' + lines.join('\n');
                    }
                } else if (response.data.errors) {
                    const errorDetails = [];
                    Object.keys(response.data.errors).forEach(field => {
                        const fieldErrors = response.data.errors[field];
                        if (Array.isArray(fieldErrors)) {
                            fieldErrors.forEach(error => errorDetails.push(error));
                        } else {
                            errorDetails.push(fieldErrors);
                        }
                    });
                    if (errorDetails.length > 0) {
                        errorMessage += '\n\nالتفاصيل:\n' + errorDetails.join('\n');
                    }
                }
                
                setUploadError(errorMessage);
            }
        } catch (error) {
            let errorMessage = 'فشل في رفع ملف Excel';
            
            if (error.response?.data) {
                if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
                
                // معالجة الأخطاء التفصيلية من الـ API
                if (error.response.data?.data?.errors && typeof error.response.data.data.errors === 'object') {
                    const rows = error.response.data.data.errors;
                    const lines = [];
                    Object.values(rows).forEach((msgs) => {
                        if (Array.isArray(msgs)) {
                            lines.push(...msgs);
                        } else if (msgs) {
                            lines.push(String(msgs));
                        }
                    });
                    if (lines.length > 0) {
                        errorMessage += '\n\nالتفاصيل:\n' + lines.join('\n');
                    }
                } else if (error.response.data.errors) {
                    const errorDetails = [];
                    Object.keys(error.response.data.errors).forEach(field => {
                        const fieldErrors = error.response.data.errors[field];
                        if (Array.isArray(fieldErrors)) {
                            fieldErrors.forEach(error => errorDetails.push(error));
                        } else {
                            errorDetails.push(fieldErrors);
                        }
                    });
                    
                    if (errorDetails.length > 0) {
                        errorMessage += '\n\nالتفاصيل:\n' + errorDetails.join('\n');
                    }
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setUploadError(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container content-wrapper">
                    <div className="text-center py-5">
                        <LoadingSpinner />
                        <p className="mt-3 text-muted">جاري تحميل المنتجات...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container content-wrapper">
                    <div className="text-center py-5">
                        <div className="alert alert-danger">
                            <h4>خطأ</h4>
                            <p>{error}</p>
                            <button 
                                className="btn btn-primary"
                                onClick={() => window.location.reload()}
                            >
                                إعادة المحاولة
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">

            <div className="dashboard-container content-wrapper">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0" style={{ fontSize: '20px' }}>المنتجات</h2>
                    <div className="d-flex gap-2">
                        {getUserRole() !== 'pharmacy_branch' && (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-success d-flex align-items-center"
                                style={{
                                    backgroundColor: '#28a745',
                                    border: 'none',
                                    borderRadius: '8px'
                                }}
                                onClick={() => setShowUploadModal(true)}>
                                <FaFileExcel className="mx-2" />
                                رفع Excel Sheet
                            </motion.button>
                        )}
                        {getUserRole() !== 'pharmacy_branch' && (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-danger d-flex align-items-center"
                                style={{
                                    backgroundColor: '#dc3545',
                                    border: 'none',
                                    borderRadius: '8px'
                                }}
                                onClick={handleBulkDeleteClick}
                                disabled={selectedProducts.length === 0 || isBulkDeleting}
                            >
                                {isBulkDeleting ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm mx-2" role="status"></div>
                                        جاري الحذف...
                                    </>
                                ) : (
                                    <>
                                        <FaTrash className="mx-2" />
                                        حذف المحدد ({selectedProducts.length})
                                    </>
                                )}
                            </motion.button>
                        )}
                        {getUserRole() !== 'pharmacy_branch' && (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-primary d-flex align-items-center"
                                style={{
                                    backgroundColor: '#184194',
                                    border: 'none',
                                    borderRadius: '8px'
                                }}
                                onClick={() => navigate('/pharmacy-products/add')}>
                                <FaPlus className="mx-2" />
                                إضافة منتج جديد
                            </motion.button>
                        )}
                    </div>
                </div>

                {/* Search and Filter Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                            <input
                                type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في المنتجات..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(searchTerm.trim() || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white mx-2">
                                            {table.getFilteredRowModel().rows.length} من {products?.length || 0} نتيجة
                                        </span>
                                    )}
                                <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                            setFilters({ status: '', subcategory_id: '' });
                                            setSelectedProducts([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                </button>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">فلترة حسب الحالة:</label>
                                        <select
                                            className="form-select"
                                            value={filters.status}
                                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                              style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                        >
                                            <option value="">جميع الحالات</option>
                                            <option value="active">مفعل</option>
                                            <option value="inactive">غير مفعل</option>
                                        </select>
                        </div>
                           {/* <div className="col-md-6  mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الفئة الفرعية *
                                    </label>
                                    <select
                                        className="form-control"
                                        name="subcategory_id"
                                        value={subcategory_id}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}>
                                        <option value="">اختر الفئة الفرعية</option>
                                        {subCategories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div> */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الفئة الفرعية *
                                    </label>
                                    <select
                                        className="form-select"
                                        value={filters.subcategory_id}
                                        onChange={(e) => setFilters(prev => ({ ...prev, subcategory_id: e.target.value }))}
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <option value="">اختر الفئة الفرعية</option>
                                        {subCategories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                    </div>
                            </motion.div>
                        )}
                </div>
                </motion.div>

                {/* Searching Indicator */}
                {searching && (
                    <div className="d-flex align-items-center justify-content-center mb-3" style={{ gap: '10px' }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">جاري البحث...</span>
                        </div>
                        <span className="text-muted" style={{ fontSize: '14px' }}>جاري البحث...</span>
                    </div>
                )}

                {/* Modern Data Table */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                        className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {selectedProducts.length > 0 && (
                            <div className="alert alert-info py-2 px-3 m-3 d-flex justify-content-between align-items-center" style={{ borderRadius: '8px', fontSize: '14px' }}>
                                <div>
                                    <FaInfoCircle className="mx-2" />
                                    {isAllSelected ? (
                                        <span>تم تحديد جميع العناصر <strong>({selectedProducts.length} عنصر)</strong> في كل الصفحات.</span>
                                    ) : (
                                        <span>تم تحديد <strong>{selectedProducts.length} عنصر</strong> من هذه الصفحة.</span>
                                    )}
                                </div>
                                {selectedProducts.length < (pagination?.total || 0) && !isAllSelected && (
                                    <button 
                                        className="btn btn-link btn-sm p-0 fw-bold" 
                                        onClick={() => handleSelectAll(false)}
                                        style={{ textDecoration: 'none', color: '#0c5460' }}
                                    >
                                        تحديد كل المنتجات ({pagination?.total || 0})
                                    </button>
                                )}
                            </div>
                        )}
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">جاري التحميل...</span>
                                </div>
                            </div>
                        ) : !Array.isArray(products) || products.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <FaBox size={48} className="mb-3" />
                                    <p>لا توجد منتجات في الوقت الحالي</p>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ 
                                overflowX: 'auto', 
                                overflowY: 'visible',
                                maxHeight: 'none'
                            }}>
                                <table className="table mb-0 table-modern" style={{ 
                                    backgroundColor: 'transparent', 
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)',
                                    minWidth: '1200px',
                                    width: '100%'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                        </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                            style={{ 
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize()
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Table Pagination */}
                    {table.getPageCount() > 1 && (
                        <div className="card-footer bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="text-muted">عرض</span>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 'auto' }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                                        >
                                            {[5, 10, 20, 30, 50].map(pageSize => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-muted">
                                            من {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} إلى{' '}
                                            {Math.min(
                                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                                table.getFilteredRowModel().rows.length
                                            )}{' '}
                                            من أصل {table.getFilteredRowModel().rows.length} نتيجة
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-end gap-2 flex-wrap">
                                                            <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            الأولى
                                                            </button>
                                                                <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            السابقة
                                                                </button>
                                        <span className="d-flex align-items-center px-3">
                                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                        </span>
                                                                <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            التالية
                                                                </button>
                                                                <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            الأخيرة
                                                                </button>
                                                    </div>
                            </div>
                                        </div>
                                        </div>
                    )}

                {/* API Pagination */}
                {pagination && (pagination.last_page > 1 || pagination.total_pages > 1) && (
                    <div className="card-footer bg-light py-2 px-2">
                        <div className="row align-items-center g-0">
                            <div className="col-md-6 px-2">
                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                    <div className="input-group" style={{ width: 'auto' }}>
                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                            #
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    const totalPages = pagination?.total_pages || pagination?.last_page || 1;
                                                    if (value <= totalPages) {
                                                        handlePageChange(value);
                                                    }
                                                }
                                            }}
                                            min="1"
                                            max={pagination?.total_pages || pagination?.last_page || 1}
                                            style={{ width: '60px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 px-2">
                                <div className="d-flex justify-content-end gap-1 flex-wrap">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage <= 1}
                                    >
                                        الأولى
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        السابقة
                                    </button>
                                    <span className="d-flex align-items-center px-3">
                                        صفحة {pagination.current_page || 1} من {pagination.total_pages || pagination.last_page || 1}
                                    </span>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        التالية
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(pagination.total_pages || pagination.last_page || 1)}
                                        disabled={currentPage >= (pagination.total_pages || pagination.last_page || 1)}
                                    >
                                        الأخيرة
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>

                {/* Excel Upload Modal */}
                {showUploadModal && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title d-flex align-items-center">
                                        <FaFileExcel className="mx-2 text-success" />
                                        رفع ملف Excel
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            setExcelFile(null);
                                            setUploadError(null);
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="excelFile" className="form-label">
                                            اختر ملف Excel (.xlsx, .xls, .csv)
                                        </label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            id="excelFile"
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleExcelFileChange}
                                        />
                                        {excelFile && (
                                            <div className="mt-2">
                                                <small className="text-success d-flex align-items-center">
                                                    <FaFileExcel className="mx-1" />
                                                    تم اختيار: {excelFile.name}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* عرض الأخطاء */}
                                    {uploadError && (
                                        <div className="alert alert-danger mt-3">
                                            <strong>خطأ في الرفع:</strong>
                                            <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
                                                {uploadError}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            setExcelFile(null);
                                            setUploadError(null);
                                        }}
                                        disabled={isUploading}
                                    >
                                        إلغاء
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success d-flex align-items-center"
                                        onClick={handleExcelUpload}
                                        disabled={!excelFile || isUploading}
                                    >
                                        {isUploading ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm mx-2" role="status">
                                                    <span className="visually-hidden">جاري الرفع...</span>
                                                </div>
                                                جاري الرفع...
                                            </>
                                        ) : (
                                            <>
                                                <FaUpload className="mx-1" />
                                                رفع الملف
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Delete Confirm Modal */}
                {deleteModalOpen && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
                            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                                    <h5 className="modal-title mb-0">تأكيد الحذف</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setDeleteModalOpen(false)}></button>
                                </div>
                                <div className="modal-body text-center p-4">
                                    <p className="mb-2">هل تريد حذف هذا المنتج؟</p>
                                    <div className="text-muted">{deleteTarget?.name}</div>
                                </div>
                                <div className="modal-footer justify-content-center">
                                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteModalOpen(false)}>إلغاء</button>
                                    <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deletingId === deleteTarget?.id}>
                                        {deletingId === deleteTarget?.id ? 'جار الحذف...' : 'حذف'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Bulk Delete Confirm Modal */}
                {bulkDeleteModalOpen && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
                            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc3545 0%, #a71d2a 100%)', color: 'white' }}>
                                    <h5 className="modal-title mb-0 d-flex align-items-center">
                                        <FaTrash className="mx-2" />
                                        تأكيد الحذف
                                    </h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setBulkDeleteModalOpen(false)}></button>
                                </div>
                                <div className="modal-body text-center p-4">
                                    <div className="mb-3">
                                        <FaTrash size={48} className="text-danger mb-3" />
                                        <p className="fw-bold mb-2" style={{ fontSize: '18px' }}>هل أنت متأكد من حذف المنتجات المحددة؟</p>
                                        <p className="text-muted mb-0">لا يمكن التراجع عن هذا الإجراء بعد التنفيذ.</p>
                                    </div>
                                    <div className="alert alert-danger py-2" style={{ fontSize: '14px' }}>
                                        <strong>عدد المنتجات المختارة:</strong> {selectedProducts.length}
                                    </div>
                                </div>
                                <div className="modal-footer justify-content-center border-0 pb-4">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary px-4" 
                                        style={{ borderRadius: '8px' }}
                                        onClick={() => setBulkDeleteModalOpen(false)}
                                        disabled={isBulkDeleting}
                                    >
                                        إلغاء
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-danger px-4 d-flex align-items-center" 
                                        style={{ borderRadius: '8px' }}
                                        onClick={confirmBulkDelete} 
                                        disabled={isBulkDeleting}
                                    >
                                        {isBulkDeleting ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm mx-2" role="status"></div>
                                                جارٍ الحذف...
                                            </>
                                        ) : (
                                            <>
                                                <FaTrash className="mx-1" />
                                                تأكيد الحذف
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PharmacyProducts;

// CSS للجدول المحسن
const styles = `
.table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 1200px;
}

.table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    vertical-align: middle;
}

.table-modern tbody tr {
    border-bottom: 1px solid #f1f3f4;
    transition: all 0.2s ease;
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    max-width: 200px;
    min-width: 120px;
}

/* منع تداخل النص مع الأعمدة المجاورة */
.table-modern td:nth-child(2) {
    max-width: 300px;
    min-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
}

.table-modern td:nth-child(2) h6 {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0 auto;
}

.table-modern td:nth-child(3) {
    min-width: 120px;
    max-width: 150px;
    text-align: center;
}

.action-buttons .btn {
    margin: 2px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s ease;
    border: none;
    min-width: 80px;
}

.action-buttons .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.card-modern:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-height: none;
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 8px;
    width: 0;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.1);
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #184194 #f1f1f1;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 8px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 0.75rem 1rem;
}

.search-input {
    border: none;
    padding: 0.75rem 1rem;
    font-size: 14px;
    background: #fff;
}

.search-input:focus {
    box-shadow: none;
    border: none;
    outline: none;
}

.btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;
    transition: all 0.2s ease;
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
    color: white;
    transform: translateY(-1px);
}

.btn-outline-primary {
    border-color: #184194;
    color: #184194;
    transition: all 0.2s ease;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 4px;
    }
    
    .action-buttons .btn {
        width: 100%;
        font-size: 11px;
        padding: 4px 6px;
    }
    
    .table-modern {
        min-width: 800px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    .table-modern td:nth-child(2) {
        max-width: 200px;
        min-width: 150px;
        text-align: center;
    }
    
    .table-modern td:nth-child(2) h6 {
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    
    .table-modern td:nth-child(3) {
        min-width: 100px;
        max-width: 120px;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
    
    .page-input-custom {
        min-width: 60px;
        max-width: 80px;
        font-size: 12px;
    }
    
    .page-go-btn {
        padding: 0.5rem 0.75rem;
        font-size: 12px;
    }
}

/* Page Input Styling */
.page-input-container {
    display: flex;
    align-items: center;
}

.page-input-form {
    margin: 0;
}

.page-input-custom {
    border: 2px solid #e9ecef;
    border-radius: 8px 0 0 8px;
    padding: 0.5rem 0.75rem;
    font-size: 14px;
    font-weight: 500;
    color: #495057;
    background: #fff;
    transition: all 0.3s ease;
    min-width: 80px;
    max-width: 100px;
}

.page-input-custom:focus {
    border-color: #184194;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25);
    outline: none;
}

.page-input-icon {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 2px solid #e9ecef;
    border-right: none;
    border-radius: 8px 0 0 8px;
    color: #6c757d;
    font-weight: bold;
    padding: 0.5rem 0.75rem;
    transition: all 0.3s ease;
}

.page-go-btn {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: 2px solid #184194;
    border-radius: 0 8px 8px 0;
    color: white;
    font-weight: 600;
    padding: 0.5rem 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

.page-go-btn:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    border-color: #0f2a6b;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(24, 65, 148, 0.4);
}

.page-go-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

/* Pagination Wrapping */
.pagination {
    flex-wrap: wrap;
    justify-content: center;
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
